import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Latex from 'react-latex-next';
import 'katex/dist/katex.min.css';
import { API_BASE_URL } from '../../config';


// Mock Data (Leaderboard only for now)
const RECENT_LEADERBOARD = [
  { rank: 1, name: "Arjun Mehta", score: "284/300", accuracy: "94%", id: "PEN-210" },
  { rank: 2, name: "Sneha Iyer", score: "278/300", accuracy: "91%", id: "PEN-912" },
  { rank: 3, name: "Rohan Das", score: "275/300", accuracy: "89%", id: "PEN-405" },
];

// Safe Rich-Text KaTeX Parser
const renderRichText = (rawText, useColorFilter = false) => {
  if (!rawText) return null;
  const str = String(rawText);
  if (!str.includes('<')) return <Latex>{str}</Latex>;
  
  let html = str;
  const cleanMath = (m) => m.replace(/&nbsp;/g, ' ').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&').replace(/<[^>]+>/g, '');
  try {
    html = html.replace(/\$\$([\s\S]*?)\$\$/g, (match, math) => {
      try { return window.katex ? window.katex.renderToString(cleanMath(math), { displayMode: true, throwOnError: false }) : match; } 
      catch (e) { return match; }
    });
    html = html.replace(/(?<!\$)\$([^$]+)\$(?!\$)/g, (match, math) => {
      try { return window.katex ? window.katex.renderToString(cleanMath(math), { displayMode: false, throwOnError: false }) : match; } 
      catch (e) { return match; }
    });
  } catch (err) {}
  
  // Apply dynamic color filtering to images if requested
  if (useColorFilter) {
    // Robustly find img tags and inject/update the style attribute to use our SVG filter
    html = html.replace(/<img\s+([\s\S]*?)>/gi, (match, attrs) => {
      if (/style=["']/i.test(attrs)) {
        return `<img ${attrs.replace(/style=(["'])/i, `style=$1filter: url(#presentable-image-filter); `)}>`;
      }
      return `<img ${attrs} style="filter: url(#presentable-image-filter); ">`;
    });
  }
  
  return <div dangerouslySetInnerHTML={{ __html: html }} className="prose prose-sm max-w-none [&_p]:m-0 inline-block text-inherit" />;
};

const HtmlEditor = ({
  value,
  onChange,
  placeholder,
  minHeightClass = 'min-h-[96px]',
  paddingClass = 'p-4',
  textClassName = 'text-sm'
}) => {
  const editorRef = React.useRef(null);
  const [resizingImg, setResizingImg] = React.useState(null);

  React.useEffect(() => {
    const el = editorRef.current;
    const nextValue = value || '';

    if (el && el.innerHTML !== nextValue) {
      el.innerHTML = nextValue;
    }
  }, [value]);

  const handlePaste = async (e) => {
    const items = e.clipboardData.items;
    for (const item of items) {
      if (item.type.indexOf('image') !== -1) {
        const blob = item.getAsFile();
        const formData = new FormData();
        formData.append('file', blob);
        formData.append('title', 'Pasted Image');
        formData.append('subject', 'General');
        formData.append('type', 'Image');

        try {
          const res = await axios.post(`${API_BASE_URL}/api/files/upload`, formData);
          const imageUrl = `${API_BASE_URL}/api/files/view/${res.data.id}`;
          const imgHtml = `<img src="${imageUrl}" style="max-width: 100%; cursor: nwse-resize; display: inline-block; vertical-align: middle; margin: 0 4px;" />`;
          document.execCommand('insertHTML', false, imgHtml);
          if (onChange && editorRef.current) onChange(editorRef.current.innerHTML);
        } catch (err) {
          console.error("Paste upload failed", err);
          toast.error("Failed to upload pasted image");
        }
      }
    }
  };

  const handleMouseDown = (e) => {
    if (e.target.tagName === 'IMG') {
      e.preventDefault();
      const rect = e.target.getBoundingClientRect();
      setResizingImg({
        el: e.target,
        startWidth: rect.width,
        startX: e.clientX
      });
      // Visual feedback
      e.target.style.outline = "2px solid #3b82f6";
      e.target.style.opacity = "0.8";
    }
  };

  React.useEffect(() => {
    const handleMouseMove = (e) => {
      if (resizingImg) {
        const deltaX = e.clientX - resizingImg.startX;
        const newWidth = Math.max(50, resizingImg.startWidth + deltaX);
        resizingImg.el.style.width = `${newWidth}px`;
        resizingImg.el.style.height = 'auto';
      }
    };

    const handleMouseUp = () => {
      if (resizingImg) {
        resizingImg.el.style.outline = "none";
        resizingImg.el.style.opacity = "1";
        if (onChange && editorRef.current) onChange(editorRef.current.innerHTML);
        setResizingImg(null);
      }
    };

    if (resizingImg) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingImg, onChange]);

  return (
    <div className="relative">
      {!value && placeholder && (
        <div className={`pointer-events-none absolute left-4 top-4 z-10 text-slate-500 ${textClassName}`}>
          {placeholder}
        </div>
      )}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        onPaste={handlePaste}
        onMouseDown={handleMouseDown}
        onDragStart={(e) => { if (e.target.tagName === 'IMG') e.preventDefault(); }}
        className={`w-full bg-transparent ${paddingClass} ${minHeightClass} ${textClassName} text-slate-200 outline-none overflow-x-auto whitespace-pre-wrap break-words [&_p]:my-0 [&_img]:inline-block [&_img]:align-middle [&_img]:max-w-full [&_img]:cursor-nwse-resize [&_img]:transition-opacity`}
      />
    </div>
  );
};

const DIFFICULTY_LEVELS = ['Easy', 'Medium', 'Hard'];
const REMEDIAL_OPTIONS = ['Guided Video Series', 'Remedial Worksheet', 'Live Tutor Debrief'];
const DEFAULT_ADAPTIVE_CONFIG = {
  title: '',
  targetClass: '',
  subject: '',
  chapter: '',
  startDifficulty: 'Medium',
  questionLimit: '12',
  stageLength: '3',
  advanceThreshold: 70,
  dropThreshold: 40,
  remedialAction: 'Guided Video Series'
};
const DEFAULT_ADAPTIVE_PERFORMANCE = 75;

const PresentationModal = ({ initialConfig, onCancel, onGenerate }) => {
  const [localConfig, setLocalConfig] = useState(initialConfig);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-lg p-6 shadow-2xl">
        <h2 className="text-xl font-bold text-white mb-6">Presentation PDF Configuration</h2>
        
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Background Color</label>
              <div className="flex items-center gap-3">
                <input type="color" value={localConfig.bgColor} onChange={e => setLocalConfig({...localConfig, bgColor: e.target.value})} className="w-12 h-12 rounded cursor-pointer shrink-0 border-0 p-0 bg-transparent" />
                <span className="text-xs text-slate-400 font-mono">{localConfig.bgColor}</span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Text Color</label>
              <div className="flex items-center gap-3">
                <input type="color" value={localConfig.textColor} onChange={e => setLocalConfig({...localConfig, textColor: e.target.value})} className="w-12 h-12 rounded cursor-pointer shrink-0 border-0 p-0 bg-transparent" />
                <span className="text-xs text-slate-400 font-mono">{localConfig.textColor}</span>
              </div>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">First Page Title</label>
            <input type="text" value={localConfig.title} onChange={e => setLocalConfig({...localConfig, title: e.target.value})} placeholder="e.g. Weekly Physics Quiz" className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-slate-200 focus:outline-none focus:border-amber-500" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Subtitle / Generated Text</label>
            <input type="text" value={localConfig.subtitle} onChange={e => setLocalConfig({...localConfig, subtitle: e.target.value})} placeholder="e.g. Generated: 3/30/2026" className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-slate-200 focus:outline-none focus:border-amber-500" />
          </div>
        </div>

        <div className="flex gap-4 mt-8 justify-end">
          <button type="button" onClick={onCancel} className="px-5 py-2.5 rounded-xl border border-slate-700 text-slate-300 hover:bg-slate-800 transition-colors">Cancel</button>
          <button type="button" onClick={() => onGenerate(localConfig)} className="px-6 py-2.5 rounded-xl bg-amber-500 text-white font-bold hover:bg-amber-600 transition-colors shadow-lg shadow-amber-500/20">Generate Presentation PDF</button>
        </div>
      </div>
    </div>
  );
};

// Helper to determine if a hex color is dark
const isDarkColor = (color) => {
  if (!color || color[0] !== '#') return false;
  try {
    const hex = color.slice(1);
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    // HSP color model brightness formula
    const brightness = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));
    return brightness < 127.5;
  } catch (e) {
    return false;
  }
};

export default function TestEngine() {
  const [isCreating, setIsCreating] = useState(false);
  const [printMode, setPrintMode] = useState('standard');
  const [fetchedQuestions, setFetchedQuestions] = useState([]);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [isCBAMode, setIsCBAMode] = useState(false); // Tracks if entry was from CBA card
  const [allBatches, setAllBatches] = useState([]); // Available batches for assignment
  const [activeActionMenuId, setActiveActionMenuId] = useState(null); // Which test has its menu open
  const [studentProfile, setStudentProfile] = useState(null);
  const [config, setConfig] = useState({
    title: '',
    targetClass: ''
  });
  const [filterLines, setFilterLines] = useState([
    { subject: '', chapter: '', customTags: '', difficulty: '', numQuestions: 10, questionType: '' }
  ]);
  const [metadata, setMetadata] = useState({
    subjects: [],
    subjectsMap: {},  // targetClass -> subjects[]
    classes: [],
    chaptersMap: {}, // subject -> chapters[]
    topicsMap: {},    // chapter -> topics[]
    questionTypes: [] // dynamically loaded
  });
  const [assessment, setAssessment] = useState(null);
  const [activeTests, setActiveTests] = useState([]);
  const [isAdaptiveBuilder, setIsAdaptiveBuilder] = useState(false);
  const [adaptiveConfig, setAdaptiveConfig] = useState({ ...DEFAULT_ADAPTIVE_CONFIG });
  const [adaptivePerformance, setAdaptivePerformance] = useState(DEFAULT_ADAPTIVE_PERFORMANCE);
  const [showPresentationModal, setShowPresentationModal] = useState(false);
  const [presentationConfig, setPresentationConfig] = useState({
    bgColor: '#ffffff',
    textColor: '#1e293b',
    title: '',
    subtitle: ''
  });

  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishConfig, setPublishConfig] = useState({
    testInterface: 'Standard',
    durationMins: 60
  });

  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [editFormData, setEditFormData] = useState({
    text: '',
    optionA: '',
    optionB: '',
    optionC: '',
    optionD: '',
    correctOption: '',
    difficulty: ''
  });
  
  const { instituteId } = useParams();
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem('user') || "{}");
  const isInstitute = user?.role?.toLowerCase() === 'institute' || user?.role?.toLowerCase() === 'administrator' || user?.role?.toLowerCase() === 'superadmin';
  const isTeacher = user?.role?.toLowerCase() === 'teacher';
  const isStudent = user?.role?.toLowerCase() === 'student';

  const fetchSubjects = React.useCallback(async (targetClass = '') => {
    // Use functional setState to safely read current cache without stale closure
    let cached = null;
    setMetadata(prev => {
      cached = targetClass ? prev.subjectsMap[targetClass] : null;
      return prev; // no-op read
    });
    // Wait one tick for the read to complete
    await new Promise(r => setTimeout(r, 0));
    if (targetClass && cached) {
      setMetadata(prev => ({ ...prev, subjects: cached }));
      return;
    }
    try {
      const res = await axios.get(API_BASE_URL + '/api/questions/metadata/subjects', {
        params: { targetClass }
      });
      const fetchedSubjects = res.data;
      setMetadata(prev => ({ 
        ...prev, 
        subjects: fetchedSubjects,
        subjectsMap: targetClass ? { ...prev.subjectsMap, [targetClass]: fetchedSubjects } : prev.subjectsMap
      }));
    } catch (err) {
      console.error("Failed to load subjects", err);
    }
  }, []); // stable — reads state via functional updater, no stale closure

  const fetchClasses = React.useCallback(async () => {
    try {
      const res = await axios.get(API_BASE_URL + '/api/questions/metadata/classes');
      setMetadata(prev => ({ ...prev, classes: res.data }));
    } catch (err) {
      console.error("Failed to load classes", err);
    }
  }, []);

  const fetchQuestionTypes = React.useCallback(async () => {
    try {
      const res = await axios.get(API_BASE_URL + '/api/questions/metadata/question-types');
      setMetadata(prev => ({ ...prev, questionTypes: res.data }));
    } catch (err) {
      console.error("Failed to load question types", err);
    }
  }, []);

  const fetchTests = React.useCallback(async () => {
    try {
      const res = await axios.get(API_BASE_URL + '/api/tests');
      if (Array.isArray(res.data)) {
        let tests = res.data;
        // If student, filter by assigned batches
        if (isStudent && studentProfile) {
          tests = tests.filter(t => 
            !t.assignedBatchIds || 
            t.assignedBatchIds.length === 0 || 
            t.assignedBatchIds.includes(studentProfile.batchIdStr)
          );
        }
        setActiveTests(tests);
      }
    } catch (err) {
      console.error("Failed to load tests", err);
    }
  }, [isStudent, studentProfile]);

  const fetchBatches = React.useCallback(async () => {
    try {
      const res = await axios.get(API_BASE_URL + '/api/batches');
      if (Array.isArray(res.data)) {
        setAllBatches(res.data);
      }
    } catch (err) {
      console.error("Failed to load batches", err);
    }
  }, []);

  const fetchStudentProfile = React.useCallback(async () => {
    if (!user?.email) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/api/students/profile?email=${user.email}`);
      setStudentProfile(res.data);
    } catch (err) {
      console.error("Failed to load student profile", err);
    }
  }, [user?.email]);

  React.useEffect(() => {
    // Only fetch management metadata if the user has appropriate permissions
    if (isInstitute || isTeacher) {
      fetchClasses();
      fetchQuestionTypes();
      fetchBatches();
    }
    
    if (isStudent) {
      fetchStudentProfile();
    }
    
    // Students and Staff both need to see tests, but we'll try/catch to prevent logout interceptor
    const loadTests = async () => {
      try {
        await fetchTests();
      } catch (err) {
        console.error("Critical test load failure:", err);
      }
    };
    loadTests();
    
    const handleClickOutside = () => setActiveActionMenuId(null);
    window.addEventListener('click', handleClickOutside);
    
    const inv = setInterval(fetchTests, 10000);
    return () => {
      clearInterval(inv);
      window.removeEventListener('click', handleClickOutside);
    };
  }, [fetchClasses, fetchQuestionTypes, fetchTests, fetchBatches, fetchStudentProfile, isInstitute, isTeacher, isStudent, studentProfile]);

  const fetchChapters = async (subject) => {
    if (!subject || metadata.chaptersMap[subject]) return;
    try {
      const res = await axios.get(API_BASE_URL + '/api/questions/metadata/chapters', { params: { subject } });
      setMetadata(prev => ({
        ...prev,
        chaptersMap: { ...prev.chaptersMap, [subject]: res.data }
      }));
    } catch (err) {
      console.error("Failed to load chapters", err);
    }
  };

  const handleDeleteTest = async (testId) => {
    if (!window.confirm("Are you sure you want to delete this test? This action cannot be undone.")) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/tests/${testId}`);
      toast("Test deleted successfully");
      fetchTests();
    } catch (err) {
      console.error(err);
      toast("Failed to delete test");
    }
  };

  const handleUpdateDuration = async (testId, newDuration) => {
    try {
      await axios.put(`${API_BASE_URL}/api/tests/${testId}`, { durationMins: parseInt(newDuration) });
      toast.success("Duration updated");
      fetchTests();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update duration");
    }
  };

  const handleAssignBatch = async (testId, batchIds) => {
    try {
      await axios.put(`${API_BASE_URL}/api/tests/${testId}`, { assignedBatchIds: batchIds });
      toast.success("Batches updated successfully");
      fetchTests();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update batches");
    }
  };

  const getTestPath = (test) => {
    const baseUrl = `/${instituteId}/lms`;
    if (test.type === 'CBA Mock Test') return `${baseUrl}/test-cba/${test.id}`;
    return `${baseUrl}/test-live/${test.id}`;
  };

  const handleDownloadPDF = async (test) => {
    try {
      // Fetch full question details using bulk API
      const res = await axios.post(`${API_BASE_URL}/api/questions/list`, test.questionIds);
      const formattedQs = res.data.map(q => ({
        ...q,
        options: [q.optionA, q.optionB, q.optionC, q.optionD].filter(Boolean)
      }));

      setAssessment({
        title: test.title,
        date: new Date().toLocaleDateString(),
        questions: formattedQs
      });
      setPrintMode('standard');
      setActiveActionMenuId(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to prepare PDF.");
    }
  };

  const handleDownloadPresentable = async (test) => {
    try {
      const res = await axios.post(`${API_BASE_URL}/api/questions/list`, test.questionIds);
      const formattedQs = res.data.map(q => ({
        ...q,
        options: [q.optionA, q.optionB, q.optionC, q.optionD].filter(Boolean)
      }));

      setAssessment({
        title: test.title,
        date: new Date().toLocaleDateString(),
        questions: formattedQs
      });
      setPrintMode('presentation');
      openPresentationModal(test.title, test.date);
      setActiveActionMenuId(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to prepare presentation.");
    }
  };

  const fetchTopics = async (chapter) => {
    if (!chapter || metadata.topicsMap[chapter]) return;
    try {
      const res = await axios.get(API_BASE_URL + '/api/questions/metadata/topics', { params: { chapter } });
      setMetadata(prev => ({
        ...prev,
        topicsMap: { ...prev.topicsMap, [chapter]: res.data }
      }));
    } catch (err) {
      console.error("Failed to load topics", err);
    }
  };


  const handleFetchQuestions = async (e) => {
    e.preventDefault();
    if (!config.title) {
      toast("Please provide an Assessment Title");
      return;
    }

    try {
      let allAggregatedQuestions = [];
      let allSelectedIds = [];

      const seenIds = new Set();
      for (const line of filterLines) {
        const res = await axios.get(API_BASE_URL + '/api/questions', {
          params: { 
            subject: line.subject, 
            difficulty: line.difficulty,
            chapter: line.chapter,
            customTags: line.customTags,
            questionType: line.questionType,
            targetClass: config.targetClass
          }
        });

        const lineQuestions = res.data;
        if (lineQuestions.length === 0) {
          toast(`No questions found for line: ${line.chapter || 'Any'} / ${line.questionType}`);
          continue;
        }

        // Avoid duplicates across lines using seenIds set
        const newQuestions = lineQuestions.filter(q => !seenIds.has(q.id));
        newQuestions.forEach(q => {
          seenIds.add(q.id);
          allAggregatedQuestions.push(q);
        });
        
        // Select requested number for this specific line
        const lineSelected = newQuestions.slice(0, parseInt(line.numQuestions) || 10).map(q => q.id);
        allSelectedIds = [...allSelectedIds, ...lineSelected];
      }

      if (allAggregatedQuestions.length === 0) {
        toast("No questions found matching any filters!");
        setFetchedQuestions([]);
        return;
      }

      setFetchedQuestions(allAggregatedQuestions);
      setSelectedQuestionIds(allSelectedIds);

    } catch (err) {
      console.error(err);
      toast("Failed to fetch questions from server.");
    }
  };

  const addFilterLine = () => {
    setFilterLines([...filterLines, { subject: '', chapter: '', customTags: '', difficulty: '', numQuestions: 5, questionType: '' }]);
  };

  const removeFilterLine = (index) => {
    if (filterLines.length > 1) {
      setFilterLines(filterLines.filter((_, i) => i !== index));
    }
  };

  const updateFilterLine = (index, field, value) => {
    const newLines = [...filterLines];
    newLines[index][field] = value;
    
    // Reset dependent fields and fetch metadata
    if (field === 'subject') {
      newLines[index].chapter = '';
      newLines[index].topic = '';
      fetchChapters(value);
    } else if (field === 'chapter') {
      newLines[index].topic = '';
      fetchTopics(value);
    }

    setFilterLines(newLines);
  };

  const openPresentationModal = (manualTitle, manualSubtitle) => {
    setPresentationConfig({
      bgColor: '#ffffff',
      textColor: '#1e293b',
      title: manualTitle || config.title || 'Untitled Assessment',
      subtitle: manualSubtitle || `Generated: ${new Date().toLocaleDateString()}`
    });
    setShowPresentationModal(true);
  };

  const handleBuildAssessment = async (mode = 'standard') => {
    if (selectedQuestionIds.length === 0) {
      toast("No questions selected!");
      return;
    }
    
    try {
      const selectedQs = fetchedQuestions.filter(q => selectedQuestionIds.includes(q.id));
      const testPayload = {
        title: config.title || "Untitled Assessment",
        type: isCBAMode ? "CBA Mock Test" : "Standard PDF Test",
        pattern: config.subject === "Biology" ? "NEET" : "JEE",
        durationMins: 60, // Default duration
        status: "Live",
        questionIds: selectedQs.map(q => q.id)
      };

      await axios.post(API_BASE_URL + '/api/tests', testPayload);
      toast.success("Assessment Created Successfully!");
      
      setIsCreating(false);
      setFetchedQuestions([]);
      setSelectedQuestionIds([]);
      fetchTests();
    } catch (err) {
      console.error(err);
      toast.error("Failed to create assessment.");
    }
  };

  const handlePublishDigital = async () => {
    if (!assessment || assessment.questions.length === 0) return;
    
    try {
      const testPayload = {
        title: assessment.title,
        type: publishConfig.testInterface === 'CBA' ? 'CBA Mock Test' : (config.difficulty ? "Chapter-wise" : "Mock Test"),
        pattern: config.subject === "Biology" ? "NEET" : "JEE",
        durationMins: publishConfig.durationMins || (config.numQuestions * 2),
        status: "Live",
        questionIds: assessment.questions.map(q => q.id)
      };
      
      await axios.post(API_BASE_URL + '/api/tests', testPayload);
      toast.success("Test Published Successfully!");
      setAssessment(null);
      setShowPublishModal(false);
      fetchTests();
    } catch (err) {
      console.error(err);
      toast.error("Failed to publish test digitally.");
    }
  };

  const handleEditClick = (q) => {
    setEditingQuestionId(q.id);
    setEditFormData({
      text: q.text || '',
      optionA: q.optionA || '',
      optionB: q.optionB || '',
      optionC: q.optionC || '',
      optionD: q.optionD || '',
      correctOption: q.correctOption || '',
      difficulty: q.difficulty || 'Medium'
    });
  };

  const handleCancelEdit = () => {
    setEditingQuestionId(null);
  };

  const handleSaveEdit = async (questionId) => {
    try {
      const payload = {
        text: editFormData.text,
        options: [editFormData.optionA, editFormData.optionB, editFormData.optionC, editFormData.optionD].filter(o => o !== undefined),
        correctOption: editFormData.correctOption,
        difficulty: editFormData.difficulty
      };
      
      const res = await axios.put(`${API_BASE_URL}/api/questions/${questionId}`, payload);
      const updatedQuestion = res.data;
      
      setFetchedQuestions(prev => prev.map(q => q.id === questionId ? updatedQuestion : q));
      toast.success("Question updated!");
      setEditingQuestionId(null);
    } catch (err) {
      console.error(err);
      toast.error("Update failed.");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const closeAdaptiveBuilder = () => {
    setIsAdaptiveBuilder(false);
    setIsCreating(false);
    setAdaptiveConfig({ ...DEFAULT_ADAPTIVE_CONFIG });
    setAdaptivePerformance(DEFAULT_ADAPTIVE_PERFORMANCE);
  };

  const openAdaptiveBuilder = () => {
    setIsAdaptiveBuilder(true);
    setIsCreating(false);
  };

  const handleAdaptiveConfigChange = (field, value) => {
    setAdaptiveConfig(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'subject') {
        updated.chapter = '';
        if (value) fetchChapters(value);
      }
      return updated;
    });
  };

  const handleAdaptiveThresholdChange = (field, rawValue) => {
    const numeric = Math.min(95, Math.max(5, Number(rawValue)));
    setAdaptiveConfig(prev => {
      const updated = { ...prev };
      if (field === 'advanceThreshold') {
        updated.advanceThreshold = Math.max(numeric, updated.dropThreshold + 5);
      } else {
        updated.dropThreshold = Math.min(numeric, updated.advanceThreshold - 5);
      }
      return updated;
    });
  };

  const adaptivePlan = React.useMemo(() => {
    const limit = Math.max(0, parseInt(adaptiveConfig.questionLimit, 10) || 0);
    const stageLength = Math.max(1, parseInt(adaptiveConfig.stageLength, 10) || 3);
    if (limit === 0) return [];

    const timeline = [];
    let remaining = limit;
    let stage = 1;
    let difficultyIndex = DIFFICULTY_LEVELS.indexOf(adaptiveConfig.startDifficulty);
    if (difficultyIndex === -1) difficultyIndex = 1;
    let simulatedPerformance = adaptivePerformance;
    const safeAdvance = Math.max(adaptiveConfig.advanceThreshold, adaptiveConfig.dropThreshold + 5);
    const safeDrop = Math.min(adaptiveConfig.dropThreshold, safeAdvance - 5);

    while (remaining > 0) {
      const questions = Math.min(stageLength, remaining);
      const performance = Math.min(100, Math.max(0, Math.round(simulatedPerformance)));
      const action = performance >= safeAdvance
        ? 'Push difficulty upward'
        : performance <= safeDrop
          ? `Trigger ${adaptiveConfig.remedialAction}`
          : 'Maintain pace and reinforce';

      timeline.push({
        stage,
        difficulty: DIFFICULTY_LEVELS[difficultyIndex],
        questions,
        performance,
        action
      });

      remaining -= questions;
      if (performance >= safeAdvance && difficultyIndex < DIFFICULTY_LEVELS.length - 1) difficultyIndex++;
      else if (performance <= safeDrop && difficultyIndex > 0) difficultyIndex--;

      if (performance >= safeAdvance) {
        simulatedPerformance = Math.max(50, simulatedPerformance - 8);
      } else if (performance <= safeDrop) {
        simulatedPerformance = Math.min(95, simulatedPerformance + 10);
      } else {
        simulatedPerformance += (safeAdvance - performance) / 2;
      }
      simulatedPerformance = Math.min(100, Math.max(0, simulatedPerformance));

      stage += 1;
      if (stage > 8) break;
    }

    return timeline;
  }, [adaptiveConfig, adaptivePerformance]);

  const adaptiveQuestionCount = adaptivePlan.reduce((sum, stage) => sum + stage.questions, 0);
  const projectedDuration = Math.max(12, Math.round(adaptiveQuestionCount * 2));
  const adaptivePlanReady = Boolean(
    adaptiveConfig.title.trim() &&
    adaptiveConfig.targetClass &&
    adaptiveConfig.subject &&
    adaptiveConfig.chapter &&
    adaptivePlan.length > 0
  );

  const availableAdaptiveSubjects = adaptiveConfig.targetClass
    ? (metadata.subjectsMap[adaptiveConfig.targetClass] || metadata.subjects)
    : metadata.subjects;
  const expectedQuestionLimit = Math.max(0, parseInt(adaptiveConfig.questionLimit, 10) || 0);
  const timelineProgress = expectedQuestionLimit
    ? Math.min(100, Math.round((adaptiveQuestionCount / expectedQuestionLimit) * 100))
    : 0;
  const difficultyCurve = [...new Set(adaptivePlan.map(stage => stage.difficulty))];
  const advanceSliderMin = Math.min(95, adaptiveConfig.dropThreshold + 5);
  const dropSliderMax = Math.min(Math.max(adaptiveConfig.advanceThreshold - 5, 25), 85);

  const handlePublishAdaptive = async () => {
    if (!adaptivePlanReady) {
      toast("Complete the adaptive configuration before publishing.");
      return;
    }

    try {
      const payload = {
        title: adaptiveConfig.title,
        type: 'Adaptive',
        pattern: 'Adaptive Flow',
        durationMins: projectedDuration,
        status: 'Live',
        targetClass: adaptiveConfig.targetClass,
        subjectFocus: adaptiveConfig.subject,
        chapterFocus: adaptiveConfig.chapter,
        questionLimit: adaptiveQuestionCount,
        questionIds: [],
        adaptiveConfigJson: JSON.stringify({
          ...adaptiveConfig,
          performanceBaseline: adaptivePerformance,
          adaptivePlan
        })
      };

      await axios.post(API_BASE_URL + '/api/tests', payload);
      toast("Adaptive assessment launched successfully.");
      closeAdaptiveBuilder();
      fetchTests();
    } catch (err) {
      console.error(err);
      toast("Failed to deploy adaptive assessment.");
    }
  };

  // ----------------------------------------------------------------------
  // VIEW: ASSESSMENT PRINT MODE
  // ----------------------------------------------------------------------
  if (assessment) {
    return (
      <div 
        id="presentation-print-root"
        className={`bg-slate-100 text-black min-h-screen ${printMode === 'presentation' ? 'p-0' : 'p-8'} print:p-0 absolute inset-0 z-50 overflow-y-auto print:block print:overflow-visible print:inset-auto print:h-auto`}
      >
        
        {/* Isolated Print Projection Hook */}
        <style>{`
          @media print {
            body * {
              visibility: hidden;
            }
            #presentation-print-root, #presentation-print-root * {
              visibility: visible;
            }
            #presentation-print-root {
              position: absolute;
              left: 0;
              top: 0;
              min-height: 100vh;
              height: max-content;
              overflow: visible !important;
            }
            /* OVERRIDE: Chromium fails to paginate flexbox layouts recursively. We MUST shatter ONLY outer flex wrappers */
            html, body, #root, #root > div, main, .min-h-screen {
              display: block !important;
              height: auto !important;
              min-height: 0 !important;
              overflow: visible !important;
            }
          }
        `}</style>

        {/* Dynamic SVG Filter for Presentation Mode Images */}
        <svg style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
          <filter id="presentable-image-filter">
            {/* 
              feColorMatrix maps black (0,0,0) to target RGB.
              Matrix: 
              0 0 0 R 0
              0 0 0 G 0
              0 0 0 B 0
              0 0 0 1 0
            */}
            <feColorMatrix 
              type="matrix" 
              values={`0 0 0 ${parseInt(presentationConfig.textColor.slice(1, 3), 16) / 255} 0
                      0 0 0 ${parseInt(presentationConfig.textColor.slice(3, 5), 16) / 255} 0
                      0 0 0 ${parseInt(presentationConfig.textColor.slice(5, 7), 16) / 255} 0
                      0 0 0 1 0`} 
            />
          </filter>
        </svg>

        {printMode === 'presentation' && (
           <style>{`
             @media print {
               @page { size: 16in 9in landscape; margin: 0; }
               body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
             }
           `}</style>
        )}
        {printMode === 'standard' && (
           <style>{`
             @media print {
               @page { margin: 1cm; size: auto; }
             }
           `}</style>
        )}

        {/* Print Controls (Hidden when printing) */}
        <div className="print:hidden flex justify-between items-center mb-8 border-b pb-4 p-8 bg-white shadow sticky top-0 z-10">
          <button 
            onClick={() => setAssessment(null)}
            className="px-4 py-2 border rounded hover:bg-slate-100 flex items-center gap-2"
          >
            &larr; Back to Dashboard
          </button>
          <div className="flex gap-4">
            <button 
              onClick={() => {
                setPublishConfig(prev => ({ ...prev, durationMins: assessment.questions.length * 2 }));
                setShowPublishModal(true);
              }}
              className="px-6 py-2 bg-emerald-600 text-white rounded shadow hover:bg-emerald-700 flex items-center gap-2 font-medium"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
              Publish to Students
            </button>
            <button 
              onClick={handlePrint}
              className={`px-6 py-2 text-white rounded shadow flex items-center gap-2 font-bold transition-colors ${printMode === 'presentation' ? 'bg-amber-500 hover:bg-amber-600' : 'bg-blue-600 hover:bg-blue-700'}`}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>
              {printMode === 'presentation' ? 'Export Presentation PDF' : 'Download Print PDF'}
            </button>
          </div>
        </div>

        {printMode === 'presentation' ? (
          // Presentation Slides Layout (PPT Size 16:9 ~ 1536x864 or 1920x1080)
          <div className="flex flex-col gap-12 print:gap-0 pb-24 items-center print:block print:pb-0">
            {/* Title Slide */}
            <div 
              className={`w-[1536px] h-[864px] border border-slate-300 print:border-none shadow-2xl print:shadow-none flex flex-col items-center justify-center p-20 break-after-page shrink-0 relative overflow-hidden ${isDarkColor(presentationConfig.bgColor) ? 'dark-ctx' : 'light-ctx'}`}
              style={{ backgroundColor: presentationConfig.bgColor, color: presentationConfig.textColor }}
            >
               <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-amber-500/10 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/3"></div>
               <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-sky-500/10 rounded-full blur-[80px] translate-y-1/3 -translate-x-1/4"></div>
               
               <h1 className="text-7xl font-extrabold uppercase tracking-widest mb-10 text-center max-w-5xl leading-tight relative" style={{ color: presentationConfig.textColor }}>{presentationConfig.title || assessment.title}</h1>
               <div className="flex items-center justify-center gap-12 mt-8 relative">
                 <div className="text-center">
                    <p className="text-4xl font-black">{presentationConfig.subtitle || assessment.date}</p>
                 </div>
                 <div className="w-1 h-16 bg-slate-300/30"></div>
                 <div className="text-center">
                    <p className="text-2xl font-bold uppercase tracking-widest mb-1 opacity-70">Total Questions</p>
                    <p className="text-4xl font-black">{assessment.questions.length}</p>
                 </div>
               </div>
            </div>

            {/* Question Slides */}
            {assessment.questions.map((q, i) => (
              <div 
                key={q.id} 
                className={`w-[1536px] h-[864px] border border-slate-300 print:border-none shadow-2xl print:shadow-none flex flex-col p-20 break-after-page shrink-0 relative overflow-hidden ${isDarkColor(presentationConfig.bgColor) ? 'dark-ctx' : 'light-ctx'}`} 
                style={{ backgroundColor: presentationConfig.bgColor, color: presentationConfig.textColor }}
              >
                 <div className="absolute top-0 left-0 w-full h-6 bg-gradient-to-r from-amber-500 to-orange-500"></div>
                 
                 <div className="flex items-center justify-between gap-6 mb-6 border-b-2 border-slate-500/20 pb-4 mt-2 shrink-0">
                    <div className="flex items-center gap-4">
                      <div className="bg-amber-100 text-amber-800 text-3xl font-black px-6 py-2 rounded-xl shadow-sm tracking-tight text-center min-w-[80px]">Q{i + 1}</div>
                      <div className="flex flex-col">
                        <span className="text-sm font-bold uppercase tracking-widest opacity-60">Single Choice</span>
                        <span className="text-xl font-extrabold tracking-tight" style={{ color: presentationConfig.textColor }}>{q.chapter || 'Assessment Question'}</span>
                      </div>
                    </div>
                    {/* Page Subtitle / Generated Text */}
                    {presentationConfig.subtitle && (
                      <div className="text-right">
                        <p className="text-sm font-bold uppercase tracking-[0.2em] opacity-40">{presentationConfig.subtitle}</p>
                      </div>
                    )}
                 </div>
                 
                 <div className="text-xl leading-[1.6] font-medium shrink-0 min-w-0 pr-12 mb-6" style={{ color: presentationConfig.textColor }}>
                   {renderRichText(q.text, isDarkColor(presentationConfig.bgColor))}
                 </div>

                 <div className="grid grid-cols-2 gap-y-4 gap-x-8 shrink-0 mb-4">
                   {q.options.map((opt, optIdx) => (
                     <div key={optIdx} className="flex gap-4 items-start">
                       <span className="text-lg font-bold opacity-60 shrink-0 mt-0.5" style={{ color: presentationConfig.textColor }}>
                         ({String.fromCharCode(65 + optIdx)})
                       </span>
                       <div className="text-lg leading-snug font-medium pt-0.5 break-words overflow-hidden" style={{ color: presentationConfig.textColor }}>
                         {renderRichText(opt, isDarkColor(presentationConfig.bgColor))}
                       </div>
                     </div>
                   ))}
                 </div>
                 
                 <div className="flex-1 w-full mt-2"></div>
              </div>
            ))}

            {/* Answer Key Slide */}
            <div 
              className={`w-[1536px] min-h-[864px] h-auto border border-slate-300 print:border-none shadow-2xl print:shadow-none flex flex-col p-20 break-after-page shrink-0 relative ${isDarkColor(presentationConfig.bgColor) ? 'dark-ctx' : 'light-ctx'}`} 
              style={{ backgroundColor: presentationConfig.bgColor, color: presentationConfig.textColor }}
            >
               <div className="absolute top-0 left-0 w-full h-6 bg-slate-800"></div>
               {/* Page Subtitle / Generated Text */}
               {presentationConfig.subtitle && (
                 <div className="absolute top-8 right-12">
                   <p className="text-sm font-bold uppercase tracking-[0.2em] opacity-40">{presentationConfig.subtitle}</p>
                 </div>
               )}
               <h2 className="text-6xl font-extrabold mb-16 text-center pt-8" style={{ color: presentationConfig.textColor }}>Master Answer Key</h2>
               <div className="grid grid-cols-5 gap-6 text-center text-3xl font-semibold w-full">
                 {assessment.questions.map((q, i) => (
                   <div key={q.id} className="flex justify-between items-center border-b-2 border-slate-500/20 py-6 px-8 rounded-xl hover:bg-slate-500/5">
                     <span className="opacity-70">Q{i + 1}.</span>
                     <span className="font-bold text-amber-600 bg-amber-500/10 px-6 py-2 rounded-xl">{q.correctOption}</span>
                   </div>
                 ))}
               </div>
            </div>
          </div>
        ) : (
          // Standard Document Layout (A4 Portrait)
          <div className="max-w-[21cm] mx-auto bg-white pt-8 print:pt-0 pb-20 shadow-xl print:shadow-none min-h-[29.7cm]">
            
            <div className="text-center mb-10 border-b-2 border-slate-300 pb-6 pt-4 px-12">
              <h1 className="text-3xl font-bold uppercase tracking-wider mb-2 text-slate-800">{assessment.title}</h1>
              <div className="flex justify-between text-sm font-semibold mt-6 text-slate-600">
                <span>Date: {assessment.date}</span>
                <span>Total Questions: {assessment.questions.length}</span>
                <span>Max Marks: {assessment.questions.length * 4}</span>
              </div>
            </div>

            <div className="space-y-8 px-12">
              {assessment.questions.map((q, i) => (
                <div key={q.id} className="break-inside-avoid">
                  <div className="flex gap-4">
                    <span className="font-bold text-lg shrink-0 w-8">Q{i + 1}.</span>
                    <div className="text-[1.05rem] leading-relaxed flex-1 min-w-0 text-slate-800">
                      {renderRichText(q.text)}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-y-4 gap-x-6 mt-5 pl-12 font-medium">
                    {q.options.map((opt, optIdx) => (
                      <div key={optIdx} className="flex gap-3 items-start">
                        <span className="font-semibold text-slate-500 shrink-0">({String.fromCharCode(65 + optIdx)})</span>
                        <div className="flex-1 min-w-0 text-slate-700">
                          {renderRichText(opt)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Standard Answer Key */}
            <div className="mt-20 pt-10 border-t-2 border-slate-300 break-before-page px-12 mx-auto max-w-3xl">
              <h2 className="text-2xl font-bold mb-8 text-center text-slate-800">Answer Key</h2>
              <div className="grid grid-cols-5 gap-y-2 gap-x-8 text-center bg-slate-50 border border-slate-200 p-6 rounded-xl">
                {assessment.questions.map((q, i) => (
                  <div key={q.id} className="flex justify-between border-b border-slate-200 pb-2 pt-2">
                    <span className="font-semibold text-slate-600">Q{i + 1}.</span>
                    <span className="font-bold text-blue-600">{q.correctOption}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Detailed Solutions Section */}
            {assessment.questions.some(q => q.solution) && (
              <div className="mt-20 pt-10 border-t-2 border-slate-300 break-before-page px-12 pb-20">
                <h2 className="text-2xl font-bold mb-10 text-center text-slate-800">Detailed Solutions</h2>
                <div className="space-y-12">
                  {assessment.questions.filter(q => q.solution).map((q, i) => (
                    <div key={q.id} className="break-inside-avoid border-b border-slate-200 pb-8 last:border-0">
                      <div className="flex gap-4 items-start mb-4">
                        <span className="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded">Q{assessment.questions.indexOf(q) + 1}</span>
                        <div className="text-sm font-semibold text-slate-700">Solution:</div>
                      </div>
                      <div className="text-sm leading-relaxed text-slate-600 pl-4 border-l-2 border-blue-100">
                        {renderRichText(q.solution)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        )}

        {/* Digital Publishing Configuration Modal */}
        {showPublishModal && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4 print:hidden">
            <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-md p-8 shadow-2xl animate-in zoom-in-95 duration-200">
              <h2 className="text-2xl font-bold text-white mb-2">Publish Digital Test</h2>
              <p className="text-sm text-slate-400 mb-8">Configure how students will experience this assessment.</p>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Test Interface</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button 
                      onClick={() => setPublishConfig({...publishConfig, testInterface: 'Standard'})}
                      className={`p-4 rounded-2xl border transition-all text-left ${publishConfig.testInterface === 'Standard' ? 'bg-indigo-500/10 border-indigo-500 text-white' : 'bg-slate-950/50 border-slate-800 text-slate-400 hover:border-slate-700'}`}
                    >
                      <div className="font-bold text-sm mb-1">Standard</div>
                      <div className="text-[10px] opacity-70">Sequential questions</div>
                    </button>
                    <button 
                      onClick={() => setPublishConfig({...publishConfig, testInterface: 'CBA'})}
                      className={`p-4 rounded-2xl border transition-all text-left ${publishConfig.testInterface === 'CBA' ? 'bg-indigo-500/10 border-indigo-500 text-white' : 'bg-slate-950/50 border-slate-800 text-slate-400 hover:border-slate-700'}`}
                    >
                      <div className="font-bold text-sm mb-1">CBA (NTA)</div>
                      <div className="text-[10px] opacity-70">Full JEE/NEET Palette</div>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Duration (Minutes)</label>
                  <input 
                    type="number" 
                    value={publishConfig.durationMins} 
                    onChange={e => setPublishConfig({...publishConfig, durationMins: parseInt(e.target.value) || 0})}
                    className="w-full bg-slate-950/50 border border-slate-800 rounded-xl p-4 text-white focus:outline-none focus:border-indigo-500 transition-colors"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-10">
                <button 
                  onClick={() => setShowPublishModal(false)}
                  className="flex-1 px-6 py-3 rounded-xl border border-slate-800 text-slate-400 font-bold hover:bg-slate-800 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={handlePublishDigital}
                  className="flex-1 px-6 py-3 rounded-xl bg-indigo-600 text-white font-bold hover:bg-indigo-500 transition-all shadow-lg shadow-indigo-600/20 active:scale-95"
                >
                  Confirm & Publish
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Selection Modal */}
        {showPresentationModal && (
          <PresentationModal 
            initialConfig={presentationConfig}
            onCancel={() => setShowPresentationModal(false)}
            onGenerate={(newConfig) => {
              setPresentationConfig(newConfig);
              setShowPresentationModal(false);
            }}
          />
        )}
      </div>
    );
  }

  // ----------------------------------------------------------------------
  // VIEW: CREATION FORM
  // ----------------------------------------------------------------------
  if (isAdaptiveBuilder) {
    return (
      <div className="max-w-[1400px] mx-auto space-y-6 pb-12 animate-in fade-in slide-in-from-bottom-4 duration-500 print:hidden">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-white">Adaptive Assessment Engine</h1>
            <p className="text-sm text-slate-400 mt-1 max-w-2xl">
              Design dynamic question flows that react to student performance, push difficulty when they sail through, and automatically insert remedial actions when they need support.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            {!adaptivePlanReady && (
              <p className="text-[11px] text-amber-400/80 text-right">
                {[
                  !adaptiveConfig.title.trim() && '• Assessment title',
                  !adaptiveConfig.targetClass && '• Target class',
                  !adaptiveConfig.subject && '• Subject',
                  !adaptiveConfig.chapter && '• Chapter',
                  adaptivePlan.length === 0 && '• Set a valid question limit',
                ].filter(Boolean).join('  ')}
              </p>
            )}
            <div className="flex gap-3">
              <button
                onClick={closeAdaptiveBuilder}
                className="px-4 py-2 rounded-xl border border-slate-700 text-slate-400 hover:text-white transition-colors"
              >
                Back to Dashboard
              </button>
              <button
                onClick={handlePublishAdaptive}
                disabled={!adaptivePlanReady}
                title={
                  !adaptivePlanReady
                    ? `Complete required fields to launch: ${[
                        !adaptiveConfig.title.trim() && 'Title',
                        !adaptiveConfig.targetClass && 'Target Class',
                        !adaptiveConfig.subject && 'Subject',
                        !adaptiveConfig.chapter && 'Chapter',
                      ].filter(Boolean).join(', ')}`
                    : 'Launch the adaptive assessment'
                }
                className={`px-5 py-2.5 rounded-xl font-semibold text-sm transition-all flex items-center gap-2 ${
                  adaptivePlanReady
                    ? 'bg-gradient-to-r from-teal-500 to-cyan-500 shadow-lg shadow-teal-500/30 text-white animate-pulse'
                    : 'bg-slate-800 text-slate-500 border border-slate-700 cursor-not-allowed opacity-60'
                }`}
              >
                {adaptivePlanReady ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="5 3 19 12 5 21 5 3"/>
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                )}
                Launch Adaptive Session
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">

            <div className="rounded-3xl border border-slate-800 bg-slate-900/40 p-6 space-y-5 dark-ctx">
              <h2 className="text-lg font-semibold text-white">Adaptive Session Details</h2>
              <div className="space-y-3">
                <label className="text-sm font-semibold text-slate-400">Assessment Title</label>
                <input
                  type="text"
                  placeholder="AI Proficiency Sprint - Physics"
                  value={adaptiveConfig.title}
                  onChange={(e) => handleAdaptiveConfigChange('title', e.target.value)}
                  className="w-full rounded-2xl border border-slate-800 bg-slate-950/40 px-4 py-3 text-sm text-white focus:border-teal-500 focus:outline-none transition-colors"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-400">Target Class / Grade</label>
                  <select
                    value={adaptiveConfig.targetClass}
                    onChange={(e) => {
                      const selection = e.target.value;
                      handleAdaptiveConfigChange('targetClass', selection);
                      handleAdaptiveConfigChange('subject', '');
                      if (selection) fetchSubjects(selection);
                    }}
                    className="w-full rounded-2xl border border-slate-800 bg-slate-950/40 px-4 py-3 text-sm text-white focus:border-teal-500 focus:outline-none transition-colors"
                  >
                    <option value="">Any Target Class</option>
                    {metadata.classes.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-400">Primary Subject</label>
                  <select
                    value={adaptiveConfig.subject}
                    onChange={(e) => handleAdaptiveConfigChange('subject', e.target.value)}
                    disabled={!availableAdaptiveSubjects.length}
                    className="w-full rounded-2xl border border-slate-800 bg-slate-950/40 px-4 py-3 text-sm text-white focus:border-teal-500 focus:outline-none transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <option value="">Any Subject</option>
                    {availableAdaptiveSubjects.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-400">Chapter Focus</label>
                <select
                  value={adaptiveConfig.chapter}
                  onChange={(e) => handleAdaptiveConfigChange('chapter', e.target.value)}
                  disabled={!adaptiveConfig.subject || !metadata.chaptersMap[adaptiveConfig.subject]}
                  className="w-full rounded-2xl border border-slate-800 bg-slate-950/40 px-4 py-3 text-sm text-white focus:border-teal-500 focus:outline-none transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <option value="">{!adaptiveConfig.subject ? 'Select a Subject First' : 'Select Chapter'}</option>
                  {(metadata.chaptersMap[adaptiveConfig.subject] || []).map(ch => (
                    <option key={ch} value={ch}>{ch}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="rounded-3xl border border-slate-800 bg-slate-900/40 p-6 space-y-5 dark-ctx">
              <h2 className="text-lg font-semibold text-white">Pacing & Difficulty</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-400">Question Limit</label>
                  <input
                    type="number"
                    min="5"
                    value={adaptiveConfig.questionLimit}
                    onChange={(e) => handleAdaptiveConfigChange('questionLimit', e.target.value)}
                    className="w-full rounded-2xl border border-slate-800 bg-slate-950/40 px-4 py-3 text-sm text-white focus:border-teal-500 focus:outline-none transition-colors"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-400">Stage Length (per adjustment)</label>
                  <input
                    type="number"
                    min="2"
                    value={adaptiveConfig.stageLength}
                    onChange={(e) => handleAdaptiveConfigChange('stageLength', e.target.value)}
                    className="w-full rounded-2xl border border-slate-800 bg-slate-950/40 px-4 py-3 text-sm text-white focus:border-teal-500 focus:outline-none transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-400">Starting Difficulty</label>
                  <select
                    value={adaptiveConfig.startDifficulty}
                    onChange={(e) => handleAdaptiveConfigChange('startDifficulty', e.target.value)}
                    className="w-full rounded-2xl border border-slate-800 bg-slate-950/40 px-4 py-3 text-sm text-white focus:border-teal-500 focus:outline-none transition-colors"
                  >
                    {DIFFICULTY_LEVELS.map(level => <option key={level} value={level}>{level}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-400">Remedial Action</label>
                  <select
                    value={adaptiveConfig.remedialAction}
                    onChange={(e) => handleAdaptiveConfigChange('remedialAction', e.target.value)}
                    className="w-full rounded-2xl border border-slate-800 bg-slate-950/40 px-4 py-3 text-sm text-white focus:border-teal-500 focus:outline-none transition-colors"
                  >
                    {REMEDIAL_OPTIONS.map(option => <option key={option} value={option}>{option}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-slate-800 bg-slate-900/40 p-6 space-y-5 dark-ctx">
              <h2 className="text-lg font-semibold text-white">Adaptive Intelligence Controls</h2>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-xs uppercase tracking-wider text-slate-500">
                    <span>Advance Threshold ({adaptiveConfig.advanceThreshold}%)</span>
                    <span className="text-teal-400">Push hard</span>
                  </div>
                  <input
                    type="range"
                    min={advanceSliderMin}
                    max="95"
                    value={adaptiveConfig.advanceThreshold}
                    onChange={(e) => handleAdaptiveThresholdChange('advanceThreshold', e.target.value)}
                    className="w-full accent-teal-400 mt-2"
                  />
                </div>

                <div>
                  <div className="flex justify-between text-xs uppercase tracking-wider text-slate-500">
                    <span>Drop Threshold ({adaptiveConfig.dropThreshold}%)</span>
                    <span className="text-rose-400">Remedial</span>
                  </div>
                  <input
                    type="range"
                    min="15"
                    max={dropSliderMax}
                    value={adaptiveConfig.dropThreshold}
                    onChange={(e) => handleAdaptiveThresholdChange('dropThreshold', e.target.value)}
                    className="w-full accent-rose-400 mt-2"
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-slate-800/60">
                <label className="text-sm font-semibold text-slate-400">Performance preview ({adaptivePerformance}%)</label>
                <input
                  type="range"
                  min="30"
                  max="95"
                  value={adaptivePerformance}
                  onChange={(e) => setAdaptivePerformance(Number(e.target.value))}
                  className="w-full accent-indigo-400 mt-2"
                />
                <p className="text-xs text-slate-500 mt-1">Slide to simulate how the planner reacts to different performance bands.</p>
              </div>
            </div>

          </div>

          <div className="space-y-6">
            <div className="rounded-3xl border border-slate-800 bg-slate-900/40 p-6 space-y-4 dark-ctx">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Projected Questions</p>
                  <p className="text-3xl font-bold text-white">{adaptiveQuestionCount}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wider text-slate-500">Estimated Duration</p>
                  <p className="text-base font-semibold text-white">{projectedDuration} mins</p>
                </div>
              </div>
              <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-400 to-cyan-500 transition-all"
                  style={{ width: `${timelineProgress}%` }}
                ></div>
              </div>
              <div className="flex items-center justify-between text-[11px] text-slate-500">
                <span>{adaptiveQuestionCount} / {expectedQuestionLimit || adaptiveQuestionCount} questions mapped</span>
                <span>{timelineProgress}% plan covered</span>
              </div>
              <p className="text-xs text-slate-400">Difficulty curve: {difficultyCurve.join(' → ') || 'Set parameters to preview'}</p>
              <p className="text-xs text-slate-500">Remedial action when dipping: <span className="text-teal-300">{adaptiveConfig.remedialAction}</span></p>
            </div>

            <div className="rounded-3xl border border-slate-800 bg-slate-900/40 p-6 dark-ctx">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-slate-200">Adaptive Timeline</h3>
                <span className="text-[11px] uppercase tracking-widest text-slate-500">Stage size: {adaptiveConfig.stageLength} Qs</span>
              </div>
              {adaptivePlan.length === 0 ? (
                <div className="text-xs text-slate-500 italic text-center py-12">Adjust the limits and subject to generate a live adaptive plan.</div>
              ) : (
                <div className="space-y-3 max-h-[520px] overflow-y-auto custom-scrollbar pr-1">
                  {adaptivePlan.map(stage => (
                    <div key={stage.stage} className="p-4 rounded-2xl border border-slate-800 bg-slate-950/40 group hover:border-teal-500 transition-colors">
                      <div className="flex items-center justify-between text-xs text-slate-400">
                        <span>Stage {stage.stage}</span>
                        <span>{stage.questions} questions</span>
                      </div>
                      <div className="text-lg font-bold text-white mt-1">{stage.difficulty}</div>
                      <div className="flex items-center justify-between text-[11px] text-slate-500 mt-2">
                        <span>Performance {stage.performance}%</span>
                        <span className="text-emerald-300">{stage.action}</span>
                      </div>
                      <div className="h-1.5 bg-slate-800 rounded-full mt-3 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-emerald-400 to-indigo-500" style={{ width: `${stage.performance}%` }}></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isCreating) {
    return (
      <div className="max-w-[1600px] mx-auto space-y-8 pb-12 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="flex justify-between items-center bg-slate-900/40 p-6 rounded-2xl border border-slate-800">
          <div>
            <h2 className="text-2xl font-bold text-white">Generate New Assessment</h2>
            <p className="text-slate-400 mt-1">Configure your filters and let the engine randomly assemble a paper from the database.</p>
          </div>
          <button onClick={() => setIsCreating(false)} className="px-5 py-2 rounded-xl border border-slate-700 hover:bg-slate-800 text-slate-300 transition-colors">
            Cancel
          </button>
        </div>

        <form onSubmit={handleFetchQuestions} className="bg-slate-900/60 p-8 rounded-3xl border border-slate-800 shadow-xl max-w-4xl mx-auto space-y-8">
          
          <div className="space-y-4">
            <label className="text-sm font-medium text-slate-400">Assessment Title / Exam Name</label>
            <input 
              type="text" 
              required
              value={config.title} 
              onChange={e => setConfig({...config, title: e.target.value})} 
              className="w-full bg-slate-950/50 border border-slate-800 rounded-xl p-4 text-lg text-slate-200 focus:outline-none focus:border-amber-500 transition-colors"
              placeholder="e.g. JEE Advanced Mock Test 1"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2 space-y-4">
              <label className="text-sm font-medium text-slate-400">Target Class / Batch Grade</label>
              <select 
                value={config.targetClass} 
                onChange={e => {
                  const newClass = e.target.value;
                  setConfig({...config, targetClass: newClass});
                  fetchSubjects(newClass);
                  // Reset filter lines to empty state as subjects might change
                  setFilterLines([{ subject: '', chapter: '', customTags: '', difficulty: '', numQuestions: 10, questionType: '' }]);
                }} 
                className="w-full bg-slate-950/50 border border-slate-800 rounded-xl p-4 text-slate-200 focus:outline-none focus:border-amber-500 transition-colors"
              >
                <option value="">Any Target Class</option>
                {metadata.classes.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <label className="text-sm font-medium text-slate-400 uppercase tracking-wider font-bold">Question Distribution / Multi-Line Rules</label>
              <button 
                type="button" 
                onClick={addFilterLine}
                className="text-xs bg-amber-500/10 text-amber-500 px-4 py-2 rounded-xl border border-amber-500/20 hover:bg-amber-500/20 transition-all font-bold flex items-center gap-2"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                Add Rule Line
              </button>
            </div>

            <div className="space-y-4">
              {filterLines.map((line, idx) => (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end bg-slate-950/40 p-4 rounded-3xl border border-slate-800/60 relative group shadow-inner">
                  
                  <div className="md:col-span-2 space-y-2">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest pl-1">Subject</label>
                    <select 
                      value={line.subject} 
                      onChange={e => updateFilterLine(idx, 'subject', e.target.value)} 
                      className="w-full bg-slate-900 border border-slate-700 rounded-xl p-2.5 text-xs text-slate-200 focus:outline-none focus:border-amber-500 h-[42px]"
                    >
                      <option value="">Any Subject</option>
                      {(config.targetClass ? (metadata.subjectsMap[config.targetClass] || []) : metadata.subjects).map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>

                  <div className="md:col-span-2 space-y-2">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest pl-1">Chapter</label>
                    <select 
                      value={line.chapter} 
                      onChange={e => updateFilterLine(idx, 'chapter', e.target.value)} 
                      disabled={!line.subject}
                      className="w-full bg-slate-900 border border-slate-700 rounded-xl p-2.5 text-xs text-slate-200 focus:outline-none focus:border-amber-500 disabled:opacity-30 h-[42px]"
                    >
                      <option value="">All Chapters</option>
                      {(metadata.chaptersMap[line.subject] || []).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>

                  <div className="md:col-span-2 space-y-2">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest pl-1">Difficulty</label>
                    <select 
                      value={line.difficulty} 
                      onChange={e => updateFilterLine(idx, 'difficulty', e.target.value)} 
                      className="w-full bg-slate-900 border border-slate-700 rounded-xl p-2.5 text-xs text-slate-200 focus:outline-none focus:border-amber-500 h-[42px]"
                    >
                      <option value="">Any Difficulty</option>
                      <option value="Easy">Easy</option>
                      <option value="Medium">Medium</option>
                      <option value="Hard">Hard</option>
                    </select>
                  </div>

                  <div className="md:col-span-2 space-y-2">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest pl-1">Type</label>
                    <select 
                      value={line.questionType} 
                      onChange={e => updateFilterLine(idx, 'questionType', e.target.value)} 
                      className="w-full bg-slate-900 border border-slate-700 rounded-xl p-2.5 text-xs text-slate-200 focus:outline-none focus:border-amber-500 h-[42px]"
                    >
                      <option value="">Any Type</option>
                      {metadata.questionTypes.map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2 space-y-2">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest pl-1">Custom Tags</label>
                    <input 
                      type="text"
                      placeholder="e.g. pyq, imp"
                      value={line.customTags} 
                      onChange={e => updateFilterLine(idx, 'customTags', e.target.value)} 
                      className="w-full bg-slate-900 border border-slate-700 rounded-xl p-2.5 text-xs text-slate-200 focus:outline-none focus:border-amber-500 placeholder:text-slate-600 h-[42px]"
                    />
                  </div>

                  <div className="md:col-span-1 space-y-2">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest pl-1">Count</label>
                    <input 
                      type="number" 
                      value={line.numQuestions} 
                      onChange={e => updateFilterLine(idx, 'numQuestions', e.target.value)} 
                      className="w-full bg-slate-900 border border-slate-700 rounded-xl p-2.5 text-xs font-bold text-amber-500 focus:outline-none focus:border-amber-500 text-center h-[42px]"
                    />
                  </div>

                  <div className="md:col-span-1 flex justify-end pb-1 pr-1">
                    <button 
                      type="button" 
                      onClick={() => removeFilterLine(idx)}
                      disabled={filterLines.length === 1}
                      className="p-2 text-slate-500 hover:text-red-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed group-hover:bg-slate-800 rounded-lg h-[42px] flex items-center justify-center"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"></path><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-8 border-t border-slate-800/60 text-right">
            <button type="submit" className="px-8 py-4 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-lg transition-colors border border-slate-700 shadow-lg flex items-center gap-3 ml-auto">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
              Search Question Bank
            </button>
          </div>
        </form>

        {fetchedQuestions.length > 0 && (
          <div className="bg-slate-900/60 p-8 rounded-3xl border border-slate-800 shadow-xl max-w-4xl mx-auto space-y-6 mt-8">
            <div className="flex justify-between items-center border-b border-slate-800 pb-4">
              <h3 className="text-xl font-bold text-slate-200">Select Questions ({selectedQuestionIds.length} chosen)</h3>
              <div className="space-x-4">
                <button type="button" onClick={() => setSelectedQuestionIds(fetchedQuestions.map(q => q.id))} className="text-sm text-blue-400 hover:text-blue-300 transition-colors">Select All</button>
                <button type="button" onClick={() => setSelectedQuestionIds([])} className="text-sm text-slate-400 hover:text-slate-300 transition-colors">Clear All</button>
              </div>
            </div>
            
            <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
              {fetchedQuestions.map(q => {
                const isEditing = q.id === editingQuestionId;
                
                if (isEditing) {
                  return (
                    <div key={q.id} className="p-6 rounded-3xl border border-amber-500/40 bg-slate-900/90 space-y-5 shadow-2xl shadow-amber-500/5 animate-in fade-in zoom-in-95 duration-200">
                      <div className="flex justify-between items-center mb-1">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></div>
                          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-500">Edit Question Mode</span>
                        </div>
                        <div className="flex gap-3">
                          <button type="button" onClick={handleCancelEdit} className="text-xs text-slate-400 hover:text-white px-4 py-2 rounded-xl border border-slate-700 hover:bg-slate-800 transition-all font-semibold uppercase tracking-wider">Cancel</button>
                          <button type="button" onClick={() => handleSaveEdit(q.id)} className="text-xs bg-amber-500 text-slate-950 px-5 py-2 rounded-xl hover:bg-amber-400 transition-all font-black uppercase tracking-wider shadow-lg shadow-amber-500/20">Save Changes</button>
                        </div>
                      </div>

                      <div className="space-y-5">
                        <div className="space-y-2">
                          <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.15em] ml-1">Question Content (Rich Text/LaTeX)</label>
                          <div className="bg-slate-950/50 border border-slate-800 rounded-2xl custom-scrollbar transition-all focus-within:border-amber-500/50">
                            <HtmlEditor
                              value={editFormData.text}
                              onChange={(value) => setEditFormData({...editFormData, text: value})}
                              placeholder="Type question content here..."
                              minHeightClass="min-h-[128px]"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {['A', 'B', 'C', 'D'].map(letter => (
                            <div key={letter} className="space-y-2">
                              <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.15em] ml-1">Option {letter}</label>
                              <div className="bg-slate-950/50 border border-slate-800 rounded-xl transition-all focus-within:border-amber-500/50">
                                <div className="px-4 pt-3 text-slate-600 font-bold text-xs">{letter}</div>
                                <HtmlEditor
                                  value={editFormData[`option${letter}`]}
                                  onChange={(value) => setEditFormData({...editFormData, [`option${letter}`]: value})}
                                  placeholder={`Type option ${letter} here...`}
                                  minHeightClass="min-h-[72px]"
                                  paddingClass="p-3 pt-1"
                                  textClassName="text-xs"
                                />
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="grid grid-cols-2 gap-4 pt-2">
                          <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.15em] ml-1">Correct Identity (e.g. A or ABD)</label>
                            <input 
                              type="text"
                              value={editFormData.correctOption}
                              onChange={e => setEditFormData({...editFormData, correctOption: e.target.value.toUpperCase()})}
                              className="w-full bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-2.5 text-xs font-bold text-amber-500 focus:outline-none focus:border-amber-500/50 transition-all"
                              placeholder="e.g. A"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.15em] ml-1">Difficulty Metric</label>
                            <select 
                              value={editFormData.difficulty}
                              onChange={e => setEditFormData({...editFormData, difficulty: e.target.value})}
                              className="w-full bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-2.5 text-xs text-slate-200 focus:outline-none focus:border-amber-500/50 transition-all h-[38px]"
                            >
                              <option value="Easy">Easy</option>
                              <option value="Medium">Medium</option>
                              <option value="Hard">Hard</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={q.id} className={`flex items-start gap-4 p-5 rounded-2xl border transition-all relative group ${selectedQuestionIds.includes(q.id) ? 'bg-amber-500/10 border-amber-500/30 shadow-lg shadow-amber-500/5' : 'bg-slate-950/50 border-slate-800/50 hover:border-slate-700'}`}>
                    <input 
                      type="checkbox" 
                      className="mt-1 w-5 h-5 accent-amber-500 cursor-pointer shrink-0 rounded-lg border-slate-700" 
                      checked={selectedQuestionIds.includes(q.id)}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedQuestionIds([...selectedQuestionIds, q.id]);
                        else setSelectedQuestionIds(selectedQuestionIds.filter(id => id !== q.id));
                      }}
                    />
                    <div 
                      className="flex-1 min-w-0" 
                      onClick={() => {
                        if (selectedQuestionIds.includes(q.id)) setSelectedQuestionIds(selectedQuestionIds.filter(id => id !== q.id));
                        else setSelectedQuestionIds([...selectedQuestionIds, q.id]);
                      }} 
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="text-sm font-medium text-slate-200 mb-4 overflow-x-auto hide-scrollbar whitespace-normal leading-relaxed">
                         {renderRichText(q.text)}
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                        {[q.optionA, q.optionB, q.optionC, q.optionD].filter(Boolean).map((opt, idx) => (
                          <div key={idx} className="flex gap-3 text-xs text-slate-400 bg-slate-900/50 p-2.5 rounded-xl border border-slate-800/50">
                            <span className="font-bold text-slate-500 shrink-0">({String.fromCharCode(65 + idx)})</span>
                            <div className="flex-1 min-w-0 overflow-x-auto hide-scrollbar whitespace-normal">
                              {renderRichText(opt)}
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-900 px-2.5 py-1 rounded-lg border border-slate-800/50">
                          <span className="w-1 h-1 rounded-full bg-slate-500"></span>
                          {q.difficulty}
                        </div>
                        {q.chapter && (
                          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-900 px-2.5 py-1 rounded-lg border border-slate-800/50">
                            {q.chapter}
                          </div>
                        )}
                        {q.questionType && (
                          <div className="text-[10px] font-bold uppercase tracking-wider text-amber-400 bg-amber-500/10 px-2.5 py-1 rounded-lg border border-amber-500/20">
                            {q.questionType}
                          </div>
                        )}
                        <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-lg border border-emerald-500/20 ml-auto">
                          Correct: {q.correctOption}
                        </div>
                      </div>
                    </div>

                    <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-all translate-x-2 group-hover:translate-x-0">
                      <button 
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleEditClick(q); }}
                        className="p-2 rounded-xl bg-slate-900 border border-slate-800 text-slate-400 hover:text-amber-400 hover:border-amber-500/30 hover:bg-amber-500/10 transition-all shadow-xl"
                        title="Edit Question"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="pt-6 mt-6 border-t border-slate-800 flex justify-end gap-4">
              <button 
                type="button"
                onClick={() => handleBuildAssessment('standard')}
                className={`px-8 py-4 rounded-xl font-bold text-lg transition-all flex items-center gap-3 ${selectedQuestionIds.length > 0 ? 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-xl shadow-orange-500/20' : 'bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700'}`}
                disabled={selectedQuestionIds.length === 0}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>
                {isCBAMode ? "Schedule & Create CBA Test" : "Schedule & Create PDF Assessment"}
              </button>
            </div>
          </div>
        )}

      {/* Selection Modal */}
      {showPresentationModal && (
        <PresentationModal 
          initialConfig={presentationConfig}
          onCancel={() => setShowPresentationModal(false)}
          onGenerate={(newConfig) => {
            setPresentationConfig(newConfig);
            setShowPresentationModal(false);
            handleBuildAssessment('presentation');
          }}
        />
      )}

      </div>
    );
  }

  // ----------------------------------------------------------------------
  // VIEW: DEFAULT DASHBOARD
  // ----------------------------------------------------------------------
  return (
    <div className="max-w-[1600px] mx-auto space-y-8 pb-12 animate-in fade-in slide-in-from-bottom-4 duration-500 print:hidden">
      
      {/* Admin/Teacher Controls */}
      {!isStudent && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-12">
          <div>
            <h1 className="text-4xl font-black text-white mb-2 flex items-center gap-4">
              <span className="bg-gradient-to-r from-orange-400 to-amber-600 bg-clip-text text-transparent">TEST ENGINE</span>
              <span className="text-xs px-2 py-1 bg-slate-800 text-slate-400 rounded-md font-mono tracking-tighter">v2.4.0_PRO</span>
            </h1>
            <p className="text-slate-400 font-medium">Create, manage, and analyze standardized assessments with adaptive AI logic.</p>
          </div>

          <div className="flex items-center gap-3">
            <button 
              onClick={() => { setIsCBAMode(false); setIsCreating(true); }}
              className="flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-2xl font-bold transition-all shadow-lg shadow-amber-500/20 active:scale-95"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>
              New Assessment
            </button>
            <div className="w-px h-8 bg-slate-800 mx-2 hidden sm:block"></div>
            <button className="p-3 bg-slate-900 border border-slate-700 text-slate-300 rounded-2xl hover:text-white hover:border-slate-500 transition-all">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33 1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82 1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>
            </button>
          </div>
        </div>
      )}

      {/* Student View Header */}
      {isStudent && (
        <div className="mb-12 text-center sm:text-left">
          <h1 className="text-4xl font-black text-white mb-2">
            <span className="bg-gradient-to-r from-sky-400 to-indigo-500 bg-clip-text text-transparent uppercase">Assessments Hub</span>
          </h1>
          <p className="text-slate-400 font-medium">Take scheduled tests, review performance, and access adaptive practice sessions.</p>
        </div>
      )}

      {/* Conditional Rendering of Dashboard vs Profile list */}
      {!isStudent ? (
        <>
          {/* Creation Hub Grid (Admin/Teacher Only) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            
            {/* Mock Tests */}
            <div className="rounded-3xl border border-orange-500/20 bg-slate-900/40 p-6 relative overflow-hidden group hover:bg-slate-800/60 transition-colors cursor-pointer" onClick={() => { setIsCBAMode(false); setIsCreating(true); }}>
              <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/10 blur-[40px] rounded-full group-hover:bg-orange-500/20 transition-all"></div>
              <div className="w-14 h-14 rounded-2xl bg-orange-500/20 text-orange-400 flex items-center justify-center mb-6">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><polyline points="16 13 8 13"></polyline><polyline points="16 17 8 17"></polyline><polyline points="10 9 9 9 8 9"></polyline></svg>
              </div>
              <h3 className="text-xl font-bold text-slate-200 mb-2">Generate PDF Tests</h3>
              <p className="text-sm text-slate-400 mb-6">Create printable document assessments instantly populated from your core question bank repository.</p>
              <div className="flex gap-2 text-xs font-semibold text-orange-400 uppercase tracking-wider">
                <span className="bg-orange-500/10 px-2.5 py-1 rounded-md">Printable</span>
                <span className="bg-orange-500/10 px-2.5 py-1 rounded-md">Auto-Fetch</span>
              </div>
            </div>

            {/* Adaptive */}
            <div
              className="rounded-3xl border border-teal-500/20 bg-slate-900/40 p-6 relative overflow-hidden group hover:bg-slate-800/60 transition-colors cursor-pointer"
              role="button"
              tabIndex={0}
              aria-label="Launch adaptive assessment builder"
              onClick={openAdaptiveBuilder}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openAdaptiveBuilder();
                }
              }}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-teal-500/10 blur-[40px] rounded-full group-hover:bg-teal-500/20 transition-all"></div>
              <div className="w-14 h-14 rounded-2xl bg-teal-500/20 text-teal-400 flex items-center justify-center mb-6">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline></svg>
              </div>
              <h3 className="text-xl font-bold text-slate-200 mb-2">Adaptive Assessments</h3>
              <p className="text-sm text-slate-400 mb-6">Let the engine dynamically adjust question difficulty based on student responses in real-time.</p>
              <div className="flex gap-2 text-xs font-semibold text-teal-400 uppercase tracking-wider">
                <span className="bg-teal-500/10 px-2.5 py-1 rounded-md">AI-Driven</span>
              </div>
            </div>

            {/* CBA Mock Tests */}
            <div 
              className="rounded-3xl border border-indigo-500/20 bg-slate-900/40 p-6 relative overflow-hidden group hover:bg-slate-800/60 transition-colors cursor-pointer" 
              onClick={() => { setIsCBAMode(true); setIsAdaptiveBuilder(false); setIsCreating(true); }}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 blur-[40px] rounded-full group-hover:bg-indigo-500/20 transition-all"></div>
              <div className="w-14 h-14 rounded-2xl bg-indigo-500/20 text-indigo-400 flex items-center justify-center mb-6">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect><line x1="8" y1="21" x2="16" y2="21"></line><line x1="12" y1="17" x2="12" y2="21"></line></svg>
              </div>
              <h3 className="text-xl font-bold text-slate-200 mb-2">CBA Mock Tests</h3>
              <p className="text-sm text-slate-400 mb-6">Deploy NTA-style digital assessments with live timers, section-wise navigation and auto-grading.</p>
              <div className="flex gap-2 text-xs font-semibold text-indigo-400 uppercase tracking-wider">
                <span className="bg-indigo-500/10 px-2.5 py-1 rounded-md">NTA-Style</span>
                <span className="bg-indigo-500/10 px-2.5 py-1 rounded-md">Live Timer</span>
                <span className="bg-indigo-500/10 px-2.5 py-1 rounded-md">Auto-Graded</span>
              </div>
            </div>

          </div>
        </>
      ) : null}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl">
                <div className="px-8 py-6 border-b border-slate-800 flex items-center justify-between bg-slate-900/50 rounded-t-3xl">
                  <h2 className="text-xl font-bold text-white flex items-center gap-3">
                    <span className="w-1.5 h-6 bg-orange-500 rounded-full"></span>
                    {isStudent ? "Upcoming & Active Tests" : "Assessment Management Dashboard"}
                  </h2>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                    <span className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest">Live Updates</span>
                  </div>
                </div>
                
                <div className="">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-slate-950/50 text-slate-500 text-[10px] font-black uppercase tracking-[0.2em] border-b border-slate-800">
                        <th className="px-8 py-4">TEST IDENTITY</th>
                        <th className="px-8 py-4">DETAILS</th>
                        <th className="px-8 py-4">STATUS</th>
                        <th className="px-8 py-4 text-right">ACTION</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/50">
                      {(activeTests && activeTests.length > 0) ? activeTests.map(test => (
                        <tr key={test.id} className="group hover:bg-slate-800/20 transition-colors">
                          <td className="px-8 py-6">
                            <div className="font-bold text-white mb-0.5 group-hover:text-orange-400 transition-colors">{test.title}</div>
                            <div className="text-[10px] font-mono text-slate-500 uppercase">{test.testIdStr || `TST-${test.id}`}</div>
                          </td>
                          <td className="px-8 py-6">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs text-slate-300 font-medium">{test.durationMins} mins</span>
                              <span className="text-slate-700">•</span>
                              <span className="text-xs text-slate-300 font-medium">{test.questionIds ? test.questionIds.length : (test.questionLimit || 0)} questions</span>
                            </div>
                            <div className="text-[10px] text-slate-500 font-bold uppercase tracking-tight">BATCHES: {test.assignedBatchIds?.length > 0 ? test.assignedBatchIds.join(', ') : 'Global Access'}</div>
                          </td>
                          <td className="px-8 py-6">
                             <div className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest bg-slate-800 border ${
                               test.status === 'Live' ? 'text-emerald-500 border-emerald-500/20' : 
                               test.status === 'Active' ? 'text-sky-500 border-sky-500/20' : 'text-slate-400 border-slate-700'
                             }`}>
                               {test.status || 'Active'}
                             </div>
                          </td>
                          <td className="px-8 py-6 text-right">
                             <div className="flex items-center justify-end gap-2">
                               {/* Primary Action: START TEST - Visible for everyone */}
                               <button 
                                 onClick={() => navigate(getTestPath(test))}
                                 className="px-4 py-1.5 bg-sky-600 hover:bg-sky-500 text-white rounded-lg text-[10px] font-black tracking-widest transition-all shadow-lg shadow-sky-900/40 flex items-center gap-2"
                               >
                                 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="opacity-80"><path d="M5 3l14 9-14 9V3z" fill="currentColor"/></svg>
                                 START TEST
                               </button>

                               {/* Staff Only Actions */}
                               {!isStudent && (
                                 <>
                                   <button 
                                      onClick={() => handleDownloadPDF(test)}
                                      className="p-2 bg-slate-800 hover:bg-slate-700 text-amber-500 rounded-lg transition-colors border border-slate-700 shadow-lg"
                                      title="Export to PDF"
                                   >
                                     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                   </button>

                                   <div className="relative">
                                     <button 
                                        onClick={(e) => { e.stopPropagation(); setActiveActionMenuId(activeActionMenuId === test.id ? null : test.id); }}
                                        className="p-2 hover:bg-slate-700 rounded-lg text-slate-400 transition-colors"
                                     >
                                       <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1"></circle><circle cx="12" cy="5" r="1"></circle><circle cx="12" cy="19" r="1"></circle></svg>
                                     </button>

                                     {activeActionMenuId === test.id && (
                                       <>
                                         <div className="fixed inset-0 z-10" onClick={() => setActiveActionMenuId(null)}></div>
                                         <div className="absolute right-0 top-10 w-48 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl py-2 z-20 animate-in fade-in zoom-in-95 duration-150">
                                           <div className="px-4 py-2 space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                                              <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest border-b border-slate-800 pb-1 mb-2">Assign Batches</div>
                                              {allBatches.map(b => (
                                                <label key={b.id} className="flex items-center gap-2 cursor-pointer group/item py-1">
                                                  <input 
                                                    type="checkbox" 
                                                    className="w-3.5 h-3.5 rounded border-slate-700 bg-slate-800 text-sky-500 focus:ring-sky-500/20 focus:ring-offset-0 cursor-pointer"
                                                    checked={test.assignedBatchIds?.includes(b.batchIdStr)}
                                                    onChange={(e) => {
                                                      const current = test.assignedBatchIds || [];
                                                      const updated = e.target.checked 
                                                        ? [...current, b.batchIdStr]
                                                        : current.filter(id => id !== b.batchIdStr);
                                                      handleAssignBatch(test.id, updated);
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                  />
                                                  <span className="text-[10px] font-bold text-slate-300 group-hover/item:text-sky-400 transition-colors">{b.name}</span>
                                                </label>
                                              ))}
                                              {allBatches.length === 0 && <div className="text-[10px] text-slate-600 italic">No batches found</div>}
                                            </div>

                                           <button 
                                             onClick={(e) => { 
                                               e.stopPropagation();
                                               const dur = window.prompt("Enter new duration (minutes):", test.durationMins);
                                               if (dur) handleUpdateDuration(test.id, dur);
                                               setActiveActionMenuId(null);
                                             }}
                                             className="w-full text-left px-4 py-2 text-xs text-emerald-500 font-bold hover:bg-emerald-500/10 transition-colors flex items-center gap-2"
                                           >
                                             <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                                             Adjust Timer
                                           </button>

                                           <button 
                                             onClick={() => { setActiveActionMenuId(null); handleDownloadPresentable(test); }}
                                             className="w-full text-left px-4 py-2 text-xs text-indigo-400 font-bold hover:bg-indigo-500/10 transition-colors flex items-center gap-2"
                                           >
                                             <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 17H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-1"/><polygon points="12 15 17 21 7 21 12 15"/></svg>
                                             Presentation Mode
                                           </button>

                                           <div className="h-px bg-slate-800 my-1 mx-2"></div>
                                           <button 
                                             onClick={() => { setActiveActionMenuId(null); handleDeleteTest(test.id); }}
                                             className="w-full text-left px-4 py-2 text-xs text-rose-500 font-bold hover:bg-rose-500/10 transition-colors flex items-center gap-2"
                                           >
                                             <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                                             Delete Permanently
                                           </button>
                                         </div>
                                       </>
                                     )}
                                   </div>
                                 </>
                               )}
                             </div>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="4" className="px-8 py-20 text-center">
                            <div className="flex flex-col items-center gap-4">
                              <div className="w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center text-slate-500">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><line x1="10" y1="9" x2="8" y2="9"></line></svg>
                              </div>
                              <p className="text-slate-400 font-medium">No tests available matching your current profile.</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="space-y-8">
              <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-2xl">
                <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-6 border-b border-slate-800 pb-4">Performance Pulse</h3>
                <div className="space-y-6">
                  {RECENT_LEADERBOARD.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-4 group">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-black text-sm ${
                        idx === 0 ? 'bg-amber-500/20 text-amber-500' : 
                        idx === 1 ? 'bg-slate-400/20 text-slate-400' : 'bg-orange-500/20 text-orange-400'
                      }`}>
                        #{item.rank}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-bold text-white group-hover:text-indigo-400 transition-colors">{item.name}</div>
                        <div className="text-[10px] text-slate-500 font-mono">{item.id}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs font-black text-white">{item.score}</div>
                        <div className="text-[10px] text-emerald-500 font-bold">{item.accuracy}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <button className="w-full mt-8 py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all">View Full Leaderboard</button>
              </div>

              {!isStudent && (
                <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-3xl p-6 shadow-2xl shadow-indigo-900/20 text-white relative overflow-hidden group">
                  <div className="absolute -right-4 -bottom-4 w-24 h-24 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>
                  <h3 className="text-xs font-black text-white/60 uppercase tracking-widest mb-3">AI PRO TIP</h3>
                  <p className="text-sm font-medium leading-relaxed mb-6">
                    Use our **Adaptive Assessment** builder to create tests that automatically adjust difficulty based on student performance in real-time.
                  </p>
                  <button onClick={() => setIsAdaptiveBuilder(true)} className="px-5 py-2 bg-white text-indigo-600 rounded-xl text-xs font-bold shadow-lg">Try Adaptive Logic</button>
                </div>
              )}
            </div>
          </div>
        </div>
    );
  }
